// Carry & Basis page — implied-carry term structure per token, styled after
// the Market Probabilities board (see ../market-probabilities/index.tsx).
//
// All columns are LIVE from ONE call per token: implied_volatility_smile
// (use-implied-vol-smile.ts, polled every 30s) supplies both the full
// multi-tenor curve (Future/DF/T) AND spot from one consistent snapshot —
// see build-carry-curve.ts for the derivations and the Basis tautology caveat.
//
// Futures Mark / Mark Δ columns (added 2026-07-11) join Deribit's real
// futures_snapshot mark prices (use-futures-snapshot.ts, same 30s cadence)
// onto each tenor by expiry (attach-futures-snapshot.ts) — the independent,
// non-tautological cross-check the Basis column's own caveat calls for.
import { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { dilettaPanelSx } from '../../components/v2/widgets/exotics-shared';
import { useImpliedVolSmile } from '../../providers/exotics-tokens/use-implied-vol-smile';
import { useFuturesSnapshot } from '../../providers/exotics-tokens/use-futures-snapshot';
import CarryCurveCard from './carry-curve-card';
import { buildCarryCurve } from './build-carry-curve';
import { attachFuturesSnapshot, extractPerpPoint } from './attach-futures-snapshot';
import { useNow } from './use-now';
import { TEXT_HEADING, TEXT_MUTED } from './chrome';

import '../market-probabilities/styles.scss';

const CARD_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '14px',
  alignItems: 'start',
};

// Boards live in Diletta as fixed token+display config today (same pattern
// Market Probabilities' board uses) rather than the full registry. Token
// list is bounded by what Deribit actually lists options + dated futures
// for — confirmed live on TEST 2026-07-11 (both latest_vol_surface and
// futures_snapshot return real tenors/instruments for all five). AVAX
// dropped 2026-07-13: implied_volatility_smile (the panel's spot source)
// errors with a backend Internal error for AVAX on every base/quote/expiry
// combination tried — not a param issue on this end, needs a backend fix.
const BOARD_TOKENS: { token: string; ccy: string; decimals: number }[] = [
  { token: 'BTC', ccy: 'USDC', decimals: 0 },
  { token: 'ETH', ccy: 'USDC', decimals: 2 },
  { token: 'SOL', ccy: 'USDC', decimals: 2 },
  { token: 'XRP', ccy: 'USDC', decimals: 4 },
];

// Per-token data loader — calls the live hooks and builds the curve, so the
// page can map a fixed token list without violating rules-of-hooks.
const CarryCurveLoader: FC<{ token: string; ccy: string; decimals: number; now: Date }> = ({
  token,
  ccy,
  decimals,
  now,
}) => {
  const { tenors, spot, isLoading, error, lastUpdated } = useImpliedVolSmile(token);
  const { futures, lastUpdated: futuresLastUpdated } = useFuturesSnapshot(`${token}_USD`);
  const curve = tenors ? attachFuturesSnapshot(buildCarryCurve(tenors, spot), futures) : [];
  const perp = extractPerpPoint(futures);
  return (
    <CarryCurveCard
      market={{ token, ccy, spot, decimals, curve, perp }}
      isLoading={isLoading}
      error={error}
      lastUpdated={lastUpdated}
      futuresLastUpdated={futuresLastUpdated}
      now={now}
    />
  );
};

const CarryBasisPage = () => {
  // Single shared 1s tick drives every card's "As Of" elapsed readout —
  // matches Market Probabilities' useNow pattern (one timer, not one per card).
  const now = useNow();
  return (
    <Box className="market-probabilities-container" sx={{ pt: 8 }}>
      <Box sx={{ px: '22px', pt: 2, pb: 3 }}>
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={1.625}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <Typography sx={{ color: TEXT_HEADING, fontWeight: 700, fontSize: 18 }}>
            Carry &amp; Basis
          </Typography>
          <Typography sx={{ color: TEXT_MUTED, fontSize: 11.5 }}>
            Implied term structure — USD/base discount curves, forward carry, and basis by tenor
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              ...dilettaPanelSx,
              px: 1.25,
              py: 0.5,
              borderRadius: '4px',
              fontSize: 11,
              color: TEXT_MUTED,
            }}
          >
            Live — refreshed every 30s
          </Box>
        </Stack>

        <Box sx={CARD_GRID_SX}>
          {BOARD_TOKENS.map((m) => (
            <CarryCurveLoader
              key={m.token}
              token={m.token}
              ccy={m.ccy}
              decimals={m.decimals}
              now={now}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CarryBasisPage;
